import { useRef, useState } from 'react';
import { InspectionTable, Pagination } from '../desktop';
import { InspectionStartDialog, RestoreConfirmDialog, DeleteConfirmDialog } from '../shared';
import { useToastStore } from '../../../stores/toast';
import { Button, Card, formatDateCompact, openDatePicker } from '@safetyhub/ui';
import type { ReportGate } from '@safetyhub/shared';
import { FilterPill } from '../../FilterPill';
import { StatTile } from '../../StatTile';
import { useAuthStore } from '../../../stores/auth';
import type { SiteAccessMembership } from '../../../utils/siteAccess';
import { useInspectionStats } from '../../../hooks/useInspectionStats';
import {
  ActivityIcon,
  BuildingIcon,
  CalendarIcon,
  CheckCircleIcon,
  CheckSquareIcon,
  ClipboardCheckIcon,
  ClockIcon,
  MapPinIcon,
  PlusIcon,
  WrenchIcon,
  XIcon,
} from '../../icons';
interface InspectionListItem {
  id: string;
  siteName: string;
  siteId: string;
  inspectionDate: string;
  inspectorName: string;
  inspectorId: string;
  photoCount: number;
  findingCount: number;
  status: string;
  contributorCount: number;
  collaborators?: { id: string; name: string }[];
  thumbnailUrl?: string;
  highestSeverity?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  /** 삭제 일시 (soft delete) */
  deletedAt?: string | null;
  /** 검토된 사진 수 (완료 가능 여부 판단용) */
  reviewedPhotosCount?: number;
  /** 이전 발견사항 중 조치결과 미입력 수 (완료 가능 여부 판단용) */
  unreviewedPreviousFindingsCount?: number;
  /** 현장에서의 점검 순서 (N차) */
  sequenceNumber?: number;
  /** 우수사례 발견사항 수 */
  bestPracticeCount?: number;
  /** 해당 현장에서 가장 최근 점검 여부 */
  isLatestInspection?: boolean;
  /**
   * 「보고서 생성」 서버 판정 — 그대로 `InspectionTable`에 넘긴다.
   *
   * ⚠️ **2026-10-09(`a8061c0c4`)부터 권한 축도 여기 들어 있다** — 서버가 `resolveCanCreateReport`로
   * 집행 식을 미러해 싣는다. 종전 서술은 「권한 축은 들어 있지 않다(서버가 `canCreateReport: true`를
   * 넘긴다) — AND의 소유자는 표의 행이다」였고, 그 AND가 집행보다 좁아 prod **46건**을 거짓 차단해
   * 걷었다. 계약 전문은 `lib/inspections.ts`의 이 필드 JSDoc.
   */
  reportGate?: ReportGate;
}

interface InProgressInspection {
  id: string;
  siteId: string;
  siteName: string;
  inspectorId: string;
  inspectorName: string;
  status: string;
  photoCount: number;
  findingCount: number;
  contributorCount: number;
  startedAt: string;
}

interface Site {
  id: string;
  name: string;
}

interface FilterState {
  siteId?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  /** 삭제된 점검 포함 여부 */
  includeDeleted?: boolean;
  /** 장소 ID 필터 */
  locationId?: string;
  /** 설비 ID 필터 */
  equipmentId?: string;
}

interface Location {
  id: string;
  name: string;
}

interface Equipment {
  id: string;
  name: string;
  managementNumber: string | null;
}

interface InspectionListDesktopProps {
  /** 점검 목록 */
  inspections: InspectionListItem[];
  /** 페이지네이션 */
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
  };
  /** 필터 */
  filters: FilterState;
  /** 현장 목록 (필터용) */
  sites: Site[];
  /** 선택된 현장의 장소 목록 (필터용) */
  locations?: Location[];
  /** 선택된 현장의 설비 목록 (필터용) */
  equipment?: Equipment[];
  /** 진행 중 점검 목록 (시작 다이얼로그용) */
  inProgressInspections: InProgressInspection[];
  /** 현재 사용자 ID */
  currentUserId: string;
  /** 현재 사용자가 조직 소유자인지 여부 */
  isOwner: boolean;
  /** 현재 멤버십 (현장 할당 여부 체크용) */
  // `InspectionTable`에 그대로 넘겨 현장 접근 판정에 쓴다. 옛 `role`은 읽지 않는다
  // — 소유자 성질은 `isOwner` 필드가 유일한 원천이다(`utils/siteAccess` doc).
  currentMembership?: SiteAccessMembership | null;
  /** 선택된 현장 ID (새 점검 시작 시) */
  selectedSiteId?: string;
  /** 로딩 상태 */
  isLoading?: boolean;
  /** 필터 변경 */
  onFilterChange: (filters: FilterState) => void;
  /** 페이지 변경 */
  onPageChange: (page: number) => void;
  /** 정렬 변경 */
  onSort: (field: string) => void;
  /** 점검 상세로 이동 */
  onViewInspection: (id: string) => void;
  /** 보고서 생성 */
  onGenerateReport: (id: string) => void;
  /** 점검 삭제 */
  onDeleteInspection: (id: string) => Promise<{ success: boolean; error?: string } | void>;
  /** 점검 복원 */
  onRestoreInspection?: (id: string) => Promise<{ success: boolean; error?: string }>;
  /** 새 점검 시작 버튼 클릭 */
  onStartNewClick: () => void;
  /** 새 점검 시작 */
  onStartNew: (siteId: string) => void;
  /** 점검 이어서 */
  onContinueInspection: (inspectionId: string) => void;
  /** 삭제된 점검 포함 토글 */
  onToggleIncludeDeleted?: (include: boolean) => void;
  /** 검토 요청 */
  onRequestReview?: (inspectionId: string) => Promise<void>;
  /** 예약 점검 시작 */
  onStartScheduled?: (id: string) => void;
  /** 현재 정렬 */
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
}

/** 상태 필터 선택지 — 빈 값('전체')은 `FilterPill`이 넣으므로 실제 상태만 적는다. */
const STATUS_FILTER_OPTIONS = [
  { value: 'SCHEDULED', label: '예약됨' },
  { value: 'IN_PROGRESS', label: '진행중' },
  { value: 'PENDING_REVIEW', label: '검토 대기' },
  { value: 'COMPLETED', label: '완료' },
];

/**
 * 기간 pill — 겉모습·치수는 `FilterPill`과 같고(38px/radius 10px/line/brand 값 태그),
 * 속만 native `<input type="date">`다. FilterPill을 그대로 쓰지 못하는 건 날짜가 **열거된
 * 선택지가 아니어서**라 select를 얹을 수 없기 때문이다.
 *
 * 컨트롤 **전면**이 달력을 여는 것이 이 컴포넌트의 요점이다. bare `<input type="date">`는
 * 브라우저가 그린 달력 글리프를 정확히 눌러야만 열려, 입력칸 본문을 눌러도 아무 일이 없다.
 * 그래서 **보이는 것은 버튼, 실제 입력은 sr-only**로 두고 버튼이 `showPicker()`를 부른다.
 * sr-only는 `display:none`이 아니라(clip) `showPicker()`가 유효하다 — 숨긴 입력을 `hidden`으로
 * 두면 브라우저가 거부한다. 그 규약의 정본은 이제 `@safetyhub/ui`의 `DateInput`이고,
 * 여는 동작은 이 파일도 그 패키지의 `openDatePicker`를 **직접 쓴다**(2026-10-27까지 여기서
 * try/catch 없이 불러 `NotAllowedError`가 미처리 예외로 샜다).
 *
 * ## ⛔ 그런데 이 자리는 공용 `DateInput`으로 «이관하지 않는다» — 재서 판정했다
 * 공용은 트리거 안에 「아이콘 + 한 줄 문구」만 그리는데 이 알약은 **라벨 + 값 배지 + 지우기
 * 버튼**이라 내용 슬롯이 아예 없다. 색 말고도 덮이지 않는 축이 셋 더 있다(2026-10-27 실측 ·
 * 생성 CSS 줄 순서): `text-[14.5px]`(79) < `text-sm`(83) · `text-ink-700` < `text-ink-900` ·
 * 아이콘 치수는 공용 내부라 손도 못 댄다. 그리고 이 컴포넌트의 치수는 **형제 `FilterPill`과
 * 맞춘 계약**이라(위 문단) 공용 밀도로 끌면 그 필터 줄의 다른 일관성이 깨진다.
 * ⇒ 남는 중복은 **시각 셸 하나**이고, 포맷·picker 규약은 공용으로 돌렸다. 이 자리를 옮기려면
 * 「트리거 내용 슬롯을 공용에 둘지」를 먼저 정해야 한다(공용 모양 보장과 맞바꾸는 결정이다).
 */
function DateFilterPill({
  label,
  value,
  onChange,
}: {
  label: string;
  /** `YYYY-MM-DD` 또는 빈 문자열. */
  value: string;
  onChange: (value: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="relative inline-flex">
      <button
        type="button"
        onClick={() => openDatePicker(inputRef.current)}
        className={`flex h-[38px] items-center gap-2 rounded-[10px] border border-line bg-white text-[14.5px] font-semibold text-ink-700 transition-colors hover:bg-surface-hover focus:outline-none focus:ring-2 focus:ring-brand-500 ${
          value ? 'pl-3 pr-8' : 'px-3'
        }`}
      >
        <CalendarIcon className="h-3.5 w-3.5 flex-none" />
        {label}
        <span className="rounded-[5px] bg-brand-50 px-1.5 py-px text-[12.5px] font-bold tracking-[.01em] tabular-nums text-brand-700">
          {value ? formatDateCompact(value) : '전체'}
        </span>
      </button>

      {/* 지우기 — pill 버튼 **안**에 두면 버튼 중첩(무효 HTML)이라 형제로 얹는다.
          sr-only 입력에는 브라우저가 주는 값 지우기 어포던스가 보이지 않으므로 이게 그 대역이다. */}
      {value && (
        <button
          type="button"
          aria-label={`${label} 지우기`}
          onClick={() => onChange('')}
          className="absolute right-1.5 top-1/2 grid h-5 w-5 -translate-y-1/2 place-items-center rounded-full text-ink-400 transition-colors hover:bg-ink-100 hover:text-ink-700 focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          <XIcon className="h-3 w-3" />
        </button>
      )}

      <input
        ref={inputRef}
        type="date"
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="sr-only"
        tabIndex={-1}
      />
    </div>
  );
}

export function InspectionListDesktop({
  inspections,
  pagination,
  filters,
  sites,
  locations = [],
  equipment = [],
  inProgressInspections,
  currentUserId,
  isOwner,
  currentMembership,
  selectedSiteId,
  isLoading = false,
  onFilterChange,
  onPageChange,
  onSort,
  onViewInspection,
  onGenerateReport,
  onDeleteInspection,
  onRestoreInspection,
  onStartNewClick,
  onStartNew,
  onContinueInspection,
  onToggleIncludeDeleted,
  onRequestReview,
  onStartScheduled,
  sortField,
  sortDirection,
}: InspectionListDesktopProps) {
  const { showToast } = useToastStore();
  const companyId = useAuthStore((state) => state.currentMembership?.companyId);
  // 통계는 상태를 뺀 목록 필터를 그대로 물려받는다 — 카드 수와 목록 수가 갈리지 않게.
  const { stats } = useInspectionStats(companyId, {
    siteId: filters.siteId,
    locationId: filters.locationId,
    equipmentId: filters.equipmentId,
    includeDeleted: filters.includeDeleted,
    startDate: filters.startDate,
    endDate: filters.endDate,
  });
  const [showStartDialog, setShowStartDialog] = useState(false);
  // 삭제된 점검 복원 확인 다이얼로그
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [deletedInspectionForRestore, setDeletedInspectionForRestore] = useState<InspectionListItem | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  // 점검 삭제 확인 다이얼로그
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [inspectionToDelete, setInspectionToDelete] = useState<InspectionListItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // 검토 요청 중인 점검 ID
  const [requestingReviewId, setRequestingReviewId] = useState<string | null>(null);

  // 새 점검 시작 버튼 클릭 처리
  const handleStartNewClick = () => {
    // 진행 중인 점검이 있으면 다이얼로그 표시
    if (inProgressInspections.length > 0 && selectedSiteId) {
      setShowStartDialog(true);
    } else {
      onStartNewClick();
    }
  };

  // 선택된 현장의 진행 중 점검
  const siteInProgressInspections = selectedSiteId
    ? inProgressInspections.filter((i) => i.siteId === selectedSiteId)
    : [];

  // 선택된 현장 정보
  const selectedSite = sites.find((s) => s.id === selectedSiteId);

  // 점검 클릭 핸들러 - 삭제 여부에 따라 분기
  const handleInspectionClick = (inspection: InspectionListItem) => {
    if (inspection.deletedAt) {
      // 삭제된 점검: 복원 확인 다이얼로그 표시
      setDeletedInspectionForRestore(inspection);
      setShowRestoreDialog(true);
    } else {
      // 활성 점검: 상세 페이지로 이동
      onViewInspection(inspection.id);
    }
  };

  // 복원 처리
  const handleRestore = async () => {
    if (!deletedInspectionForRestore || !onRestoreInspection) return;

    setIsRestoring(true);
    try {
      const result = await onRestoreInspection(deletedInspectionForRestore.id);
      setShowRestoreDialog(false);
      setDeletedInspectionForRestore(null);

      if (result.success) {
        showToast('점검이 복원되었습니다.', 'success');
      } else {
        // 복원 실패 (영구 삭제됨 등)
        showToast(result.error || '복원에 실패했습니다.', 'error');
      }
    } finally {
      setIsRestoring(false);
    }
  };

  // 복원 권한 확인
  const canRestoreInspection = (inspection: InspectionListItem) => {
    return isOwner || inspection.inspectorId === currentUserId;
  };

  // 삭제 버튼 클릭 - 확인 다이얼로그 표시
  const handleDeleteClick = (id: string) => {
    const inspection = inspections.find((i) => i.id === id);
    if (inspection) {
      setInspectionToDelete(inspection);
      setShowDeleteDialog(true);
    }
  };

  // 삭제 확인 처리
  const handleDeleteConfirm = async () => {
    if (!inspectionToDelete) return;

    setIsDeleting(true);
    try {
      const result = await onDeleteInspection(inspectionToDelete.id);
      // 성공 시에만 다이얼로그 닫기
      if (result?.success) {
        setShowDeleteDialog(false);
        setInspectionToDelete(null);
      }
      // 실패 시 다이얼로그 유지 (토스트는 hook에서 표시)
    } finally {
      setIsDeleting(false);
    }
  };

  // 검토 요청 처리
  const handleRequestReview = async (inspectionId: string) => {
    if (!onRequestReview) return;

    setRequestingReviewId(inspectionId);
    try {
      await onRequestReview(inspectionId);
    } finally {
      setRequestingReviewId(null);
    }
  };

  return (
    /* 페이지 셸은 Layout이 준다(`<main class="p-4 lg:p-6">`) — 여기서 다시 배경·최대폭·좌우
       패딩을 두면 정렬된 다른 목록(현장 관리·문서관리)과 여백이 갈린다. */
    <div className="space-y-6">
      {/* 헤더 — 목업 `.page-h`. 제목 옆 부제가 이 목록이 무엇인지 한 줄로 말한다. */}
      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="text-[26px] font-extrabold tracking-tight text-ink-900">점검 목록</h1>
          <p className="mt-1 text-[15px] font-medium text-ink-500">
            현장에서 수행한 점검 내역을 확인합니다.
          </p>
        </div>
        <Button type="button" variant="primary" onClick={handleStartNewClick}>
          <PlusIcon className="h-5 w-5" />
          새 점검 시작
        </Button>
      </div>

      {/* 통계 카드 4장 — 각 카드가 대응 상태 필터의 바로가기다(문서관리·결재함과 같은 격자).
          카드 축을 상태 pill 축과 정확히 같게 둔 덕에, 카드를 누르면 그 카드가 보여 준 수만큼의
          행이 목록에 남는다. 값을 못 읽었을 때 0으로 속이지 않고 '—'로 두는 것도 두 페이지와
          같은 규약이다. */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile
          label="전체 점검"
          value={stats ? String(stats.total) : '—'}
          unit={stats ? '건' : undefined}
          icon={ClipboardCheckIcon}
          accentClass="text-brand-600"
          iconBgClass="bg-brand-50"
          selected={!filters.status}
          onSelect={() => onFilterChange({ ...filters, status: undefined })}
        />
        <StatTile
          label="진행중"
          value={stats ? String(stats.inProgress) : '—'}
          unit={stats ? '건' : undefined}
          foot="현장에서 점검 중"
          icon={ActivityIcon}
          accentClass="text-ink-500"
          iconBgClass="bg-ink-100"
          selected={filters.status === 'IN_PROGRESS'}
          onSelect={() => onFilterChange({ ...filters, status: 'IN_PROGRESS' })}
        />
        <StatTile
          label="검토 대기"
          value={stats ? String(stats.pendingReview) : '—'}
          unit={stats ? '건' : undefined}
          foot="검토자 확인 대기 중"
          // 증감 pill 없음 — 대기 수는 "지금 쌓여 있는 양"이라 어제와 비교하려면 어제 시점의
          // 스냅샷이 있어야 한다(문서관리 '결재 대기' 카드와 같은 사유).
          icon={ClockIcon}
          accentClass="text-amber-600"
          iconBgClass="bg-amber-50"
          selected={filters.status === 'PENDING_REVIEW'}
          onSelect={() => onFilterChange({ ...filters, status: 'PENDING_REVIEW' })}
        />
        <StatTile
          label="완료"
          value={stats ? String(stats.completed) : '—'}
          unit={stats ? '건' : undefined}
          foot="검토까지 끝난 점검"
          icon={CheckSquareIcon}
          accentClass="text-emerald-600"
          iconBgClass="bg-emerald-50"
          selected={filters.status === 'COMPLETED'}
          onSelect={() => onFilterChange({ ...filters, status: 'COMPLETED' })}
        />
      </div>

      {/* 툴바 — 목업 `.toolbar`. 필터 pill 4종(현장·장소·설비·상태)과 기간 입력이 한 줄에 서고,
          결과 요약은 오른쪽 끝으로 민다. 장소·설비는 현장을 골라야 의미가 있어 그때만 나타난다. */}
      <Card radius="lg" padding="none" className="flex flex-wrap items-center gap-2.5 px-4 py-3.5">
        <FilterPill
          icon={BuildingIcon}
          label="현장"
          ariaLabel="현장 필터"
          value={filters.siteId || ''}
          options={sites.map((site) => ({ value: site.id, label: site.name }))}
          onChange={(value) => onFilterChange({ ...filters, siteId: value || undefined })}
        />

        {/* 기간 필터 */}
        <div className="flex items-center gap-2">
          <DateFilterPill
            label="시작일"
            value={filters.startDate || ''}
            onChange={(value) => onFilterChange({ ...filters, startDate: value || undefined })}
          />
          <span className="text-ink-400">~</span>
          <DateFilterPill
            label="종료일"
            value={filters.endDate || ''}
            onChange={(value) => onFilterChange({ ...filters, endDate: value || undefined })}
          />
        </div>

        {/* 장소 필터 (현장 선택 시에만 표시) */}
        {filters.siteId && locations.length > 0 && (
          <FilterPill
            icon={MapPinIcon}
            label="장소"
            ariaLabel="장소 필터"
            value={filters.locationId || ''}
            options={locations.map((loc) => ({ value: loc.id, label: loc.name }))}
            onChange={(value) => onFilterChange({ ...filters, locationId: value || undefined })}
          />
        )}

        {/* 설비 필터 (현장 선택 시에만 표시) */}
        {filters.siteId && equipment.length > 0 && (
          <FilterPill
            icon={WrenchIcon}
            label="설비"
            ariaLabel="설비 필터"
            value={filters.equipmentId || ''}
            options={equipment.map((eq) => ({
              value: eq.id,
              label: `${eq.name}${eq.managementNumber ? ` (${eq.managementNumber})` : ''}`,
            }))}
            onChange={(value) => onFilterChange({ ...filters, equipmentId: value || undefined })}
          />
        )}

        <FilterPill
          icon={CheckCircleIcon}
          label="상태"
          ariaLabel="상태 필터"
          value={filters.status || ''}
          options={STATUS_FILTER_OPTIONS}
          onChange={(value) => onFilterChange({ ...filters, status: value || undefined })}
        />

        {/* 삭제된 점검 보기 체크박스 */}
        {onToggleIncludeDeleted && (
          <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-ink-600">
            <input
              type="checkbox"
              checked={filters.includeDeleted || false}
              onChange={(e) => onToggleIncludeDeleted(e.target.checked)}
              className="h-4 w-4 rounded border-line text-brand-600 focus:ring-brand-500"
            />
            삭제된 점검 보기
          </label>
        )}

        {/* 필터 초기화 */}
        {(filters.siteId || filters.status || filters.startDate || filters.endDate || filters.includeDeleted || filters.locationId || filters.equipmentId) && (
          <button
            type="button"
            onClick={() => {
              onFilterChange({});
              onToggleIncludeDeleted?.(false);
            }}
            className="text-sm font-semibold text-ink-500 transition-colors hover:text-ink-700"
          >
            필터 초기화
          </button>
        )}

        {/* 총 건수 */}
        <p className="ml-auto text-sm font-semibold text-ink-500">
          총 <span className="font-bold tabular-nums text-ink-800">{pagination.totalCount}</span>건
        </p>
      </Card>

      {/* 테이블 */}
      <div>
        <InspectionTable
          inspections={inspections}
          isLoading={isLoading}
          currentUserId={currentUserId}
          isOwner={isOwner}
          currentMembership={currentMembership}
          sortField={sortField}
          sortDirection={sortDirection}
          requestingReviewId={requestingReviewId}
          onSort={onSort}
          onRowClick={handleInspectionClick}
          onView={onViewInspection}
          onGenerateReport={onGenerateReport}
          onRequestReview={onRequestReview ? handleRequestReview : undefined}
          onDelete={handleDeleteClick}
          onStartScheduled={onStartScheduled}
        />

        {/* 페이지네이션 */}
        {pagination.totalPages > 1 && (
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={onPageChange}
          />
        )}
      </div>

      {/* 점검 시작 다이얼로그 */}
      <InspectionStartDialog
        isOpen={showStartDialog}
        onClose={() => setShowStartDialog(false)}
        currentUserId={currentUserId}
        siteName={selectedSite?.name || ''}
        inProgressInspections={siteInProgressInspections}
        onStartNew={() => {
          if (selectedSiteId) {
            onStartNew(selectedSiteId);
          }
          setShowStartDialog(false);
        }}
        onContinue={(inspectionId) => {
          onContinueInspection(inspectionId);
          setShowStartDialog(false);
        }}
        isLoading={isLoading}
      />

      {/* 삭제된 점검 복원 확인 다이얼로그 */}
      <RestoreConfirmDialog
        isOpen={showRestoreDialog}
        onClose={() => {
          setShowRestoreDialog(false);
          setDeletedInspectionForRestore(null);
        }}
        onRestore={handleRestore}
        inspection={deletedInspectionForRestore ? {
          siteName: deletedInspectionForRestore.siteName,
          inspectionDate: deletedInspectionForRestore.inspectionDate,
          inspectorName: deletedInspectionForRestore.inspectorName,
        } : null}
        isLoading={isRestoring}
        canRestore={deletedInspectionForRestore ? canRestoreInspection(deletedInspectionForRestore) : false}
      />

      {/* 점검 삭제 확인 다이얼로그 */}
      <DeleteConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setInspectionToDelete(null);
        }}
        onDelete={handleDeleteConfirm}
        inspection={inspectionToDelete ? {
          siteName: inspectionToDelete.siteName,
          inspectionDate: inspectionToDelete.inspectionDate,
          inspectorName: inspectionToDelete.inspectorName,
        } : null}
        isLoading={isDeleting}
      />
    </div>
  );
}

export default InspectionListDesktop;
